import type { PersonFeature } from '@app/shared'
import { type Product, productForOrgKind } from '@app/shared/brand'
import { notFound } from '@tanstack/react-router'
import { featureOn } from '../features'
import {
  currentProduct,
  productsCarryingTimes,
  siteOrgWord,
} from '../public-router'

/**
 * Which products a section belongs to.
 *
 * Declared once, by the section's address, in `SECTIONS` — the nav reads it to
 * decide what to offer and the route reads it to decide what answers, so a
 * product's back office is what it is by declaration and the two cannot
 * disagree. An address with no entry belongs to every product its surface
 * serves.
 */
export type Belongs = readonly Product[]

/**
 * A shul's alone: the luach and what hangs off it, the building's screens, the
 * seats and the aliyos, a membership's tiers — the products whose orgs carry
 * the luach, read from the one place that says which those are.
 */
export const SHUL_ONLY: Belongs = productsCarryingTimes()

/**
 * A person's own documents: books, pages, decks, boards, and the features they
 * add.
 */
const STUDIO_ONLY: Belongs = ['studio']

/**
 * Every section that does not belong everywhere, by the address it answers at;
 * an address beneath one (a record, a view) belongs where the section does.
 */
const SECTIONS = {
  '/': SHUL_ONLY,
  '/zmanim': SHUL_ONLY,
  '/rules': SHUL_ONLY,
  '/notices': SHUL_ONLY,
  /* One address, two sections: a shul's pages for its door, and Studio's
     own. A charity has neither. */
  '/pages': [...SHUL_ONLY, 'studio'],
  '/board': SHUL_ONLY,
  '/members/seating': SHUL_ONLY,
  '/members/donors': SHUL_ONLY,
  '/members/model': SHUL_ONLY,
  '/billing/memberships': SHUL_ONLY,
  '/settings/rooms/layout': SHUL_ONLY,
  '/documents': STUDIO_ONLY,
  '/media': STUDIO_ONLY,
  '/motifs': STUDIO_ONLY,
  '/books': STUDIO_ONLY,
  '/otzar': STUDIO_ONLY,
  '/decks': STUDIO_ONLY,
  '/canvases': STUDIO_ONLY,
  '/type': STUDIO_ONLY,
  '/songs': STUDIO_ONLY,
} as const satisfies Record<string, Belongs>

export type Section = keyof typeof SECTIONS

/**
 * The sections in Studio that are a person's own choice to add, by the feature
 * that adds them. With the feature off the section is absent — no nav entry,
 * its address a not-found — exactly as a section of another product is. The
 * platform surface is not Studio and reaches them all.
 */
const FEATURE_SECTIONS = {
  '/otzar': 'otzar',
  '/songs': 'songs',
  '/type': 'type',
} as const satisfies Partial<Record<Section, PersonFeature>>

/**
 * The product a charity works in lists its people as donors — everyone who
 * gives — where a shul lists members, with their households and standing. One
 * address, two sections, as `/pages` is: the account a row opens is the same
 * record either way.
 */
const LISTS_DONORS: Product = productForOrgKind('chesed')

/**
 * Whether a person's shul facts are offered where this page is served: the
 * family tree and the ties between households, yahrzeits, Kohen or Levi, a
 * name to be called up by, and a membership's standing and attendance. They
 * belong where the luach does — a charity's donor has none of them, and their
 * screens state none. The platform reaches everything.
 */
export function offersShulFacts(): boolean {
  const here = currentProduct()
  return !here || SHUL_ONLY.includes(here)
}

/**
 * What a sentence about a person's record calls the org: the shul where its
 * facts are a shul's, and the product's own word for an org anywhere else.
 */
export function orgCalled(): string {
  return offersShulFacts() ? 'shul' : siteOrgWord()
}

/** Whether `/members` lists donors where this page is served. */
export function rosterIsDonors(): boolean {
  const here = currentProduct()
  return here === LISTS_DONORS
}

/**
 * Whether the section at an address belongs where this page is served. The
 * platform belongs to no product and reaches every section.
 */
export function belongsAt(address: string): boolean {
  const here = currentProduct()
  const products: Belongs | undefined =
    /* The `in` test is what makes the address one of the sections. */
    address in SECTIONS ? SECTIONS[address as Section] : undefined
  if (products && here && !products.includes(here)) return false
  const needs: PersonFeature | undefined =
    address in FEATURE_SECTIONS
      ? FEATURE_SECTIONS[address as keyof typeof FEATURE_SECTIONS]
      : undefined
  return !needs || here !== 'studio' || featureOn(needs)
}

/**
 * A route's guard: a section reached on a product it does not belong to
 * answers not found, as an address that does not exist there.
 */
export function guardAt(section: Section): () => void {
  return () => {
    if (!belongsAt(section)) throw notFound()
  }
}
